import { useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Button, Checkbox, Input, Radio, Select, Spin, Tag } from 'antd'
import {
  AudioOutlined,
  BorderOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/contexts/ToastContext'
import { useCloneVoiceMutation, useUserMedia, useVoiceClone } from '@/features/advisory-ai/hooks'
import { isVoiceCloneProcessing } from '@/features/advisory-ai/api'
import { queryKeys } from '@/api/queryKeys'

type VoiceSettingsPanelProps = {
  // Fired after a voice is successfully submitted for cloning (e.g. to close a hosting modal).
  onVoiceSaved?: () => void
  // Open straight into the recording flow instead of the overview/empty state.
  startInRecordMode?: boolean
}

const MIN_SECONDS = 10

// Read aloud while recording — a natural, phonetically varied passage (about a minute) to capture the voice.
const READING_SCRIPT = `The quick brown fox jumps over the lazy dog. Bright blue skies stretched over the quiet valley as the morning light slowly warmed the hills, and a gentle breeze carried the scent of fresh rain across the open fields. Somewhere in the distance, a train rolled steadily along the track, and the soft hum of its engine faded slowly into the calm of the afternoon. One, two, three, four, five, six, seven, eight, nine, ten. On a good day I might walk a few kilometres, read for half an hour, and still have time to cook a proper dinner before the sun goes down. I enjoy simple things like a good book, a long walk, a quiet morning with a warm cup of coffee, and a friendly conversation with an old friend. Sometimes the best moments are the ones we never planned, the small surprises that turn an ordinary day into something worth remembering. When evening comes, I like to sit by the window, watch the sky change colour, and listen to the steady rhythm of the world settling down for the night.`

function formatDuration(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${String(seconds).padStart(2, '0')}`
}

function extensionForMime(mime: string): string {
  if (mime.includes('webm')) return 'webm'
  if (mime.includes('mp4') || mime.includes('m4a')) return 'm4a'
  if (mime.includes('ogg')) return 'ogg'
  if (mime.includes('wav')) return 'wav'
  if (mime.includes('mpeg') || mime.includes('mp3')) return 'mp3'
  return 'webm'
}

const VISUALIZER_BARS = 32

// Live mic-input meter. Reads the analyser each animation frame and animates a row of bars,
// so the user can see the microphone is actually picking up sound while recording.
function MicVisualizer({ analyser, active }: { analyser: AnalyserNode | null; active: boolean }) {
  const [levels, setLevels] = useState<number[]>(() => Array(VISUALIZER_BARS).fill(0))
  const rafRef = useRef<number | null>(null)

  useEffect(() => {
    if (!analyser) {
      setLevels(Array(VISUALIZER_BARS).fill(0))
      return
    }
    const data = new Uint8Array(analyser.frequencyBinCount)
    const tick = () => {
      analyser.getByteFrequencyData(data)
      const usable = Math.floor(data.length * 0.7) // focus on the lower/voice part of the spectrum
      const step = Math.max(1, Math.floor(usable / VISUALIZER_BARS))
      const next: number[] = []
      for (let i = 0; i < VISUALIZER_BARS; i += 1) {
        let sum = 0
        for (let j = 0; j < step; j += 1) sum += data[i * step + j] ?? 0
        next.push(sum / step / 255)
      }
      setLevels(next)
      rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
  }, [analyser])

  return (
    <div className="flex items-center justify-center gap-[3px] h-20 w-full max-w-md">
      {levels.map((level, index) => (
        <div
          key={index}
          className="flex-1 rounded-full"
          style={{
            height: `${active ? Math.max(6, Math.round(level * 100)) : 6}%`,
            backgroundColor: active ? 'var(--feature-primary)' : '#d1d5db',
            transition: 'height 90ms linear',
          }}
        />
      ))}
    </div>
  )
}

export function VoiceSettingsPanel({ onVoiceSaved, startInRecordMode = false }: VoiceSettingsPanelProps) {
  const { addToast } = useToast()
  const cloneVoiceMutation = useCloneVoiceMutation()
  const queryClient = useQueryClient()
  const { data: userMedia, isLoading: clonesLoading } = useUserMedia()
  const latestClone = userMedia?.voice ?? null
  const voiceProcessing = isVoiceCloneProcessing(latestClone?.status)
  // Poll the single-clone status endpoint only while it's still processing.
  const { data: polledClone } = useVoiceClone(
    latestClone?.advisoryVoiceCloneId ?? '',
    voiceProcessing
  )
  const currentVoice = polledClone ?? latestClone
  const currentProcessing = isVoiceCloneProcessing(currentVoice?.status)

  // The combined user-media query (which drives the status tag and sample URL) doesn't poll.
  // Once the poll above sees the clone finish, refresh it so the tag flips off "Processing".
  useEffect(() => {
    if (polledClone && !isVoiceCloneProcessing(polledClone.status)) {
      void queryClient.invalidateQueries({ queryKey: queryKeys.advisory.media() })
    }
  }, [polledClone, queryClient])
  const isUpdate = !!currentVoice

  const [isRecording, setIsRecording] = useState(false)
  const [elapsed, setElapsed] = useState(0)
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null)
  const [recordedUrl, setRecordedUrl] = useState<string | null>(null)
  const [voiceName, setVoiceName] = useState('')
  const [isMale, setIsMale] = useState(true)
  const [consent, setConsent] = useState(false)
  // 'overview' = landing (existing voice or empty state); 'record' = the recording flow.
  const [mode, setMode] = useState<'overview' | 'record'>(startInRecordMode ? 'record' : 'overview')
  const [microphones, setMicrophones] = useState<MediaDeviceInfo[]>([])
  const [selectedDeviceId, setSelectedDeviceId] = useState('')
  const [analyser, setAnalyser] = useState<AnalyserNode | null>(null)
  const [isPlayingSample, setIsPlayingSample] = useState(false)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const timerRef = useRef<number | null>(null)
  const recordedUrlRef = useRef<string | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const sampleAudioRef = useRef<HTMLAudioElement | null>(null)

  const clearTimer = () => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current)
      timerRef.current = null
    }
  }

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
  }

  const teardownAudioAnalyser = () => {
    setAnalyser(null)
    if (audioContextRef.current) {
      void audioContextRef.current.close().catch(() => {})
      audioContextRef.current = null
    }
  }

  // List available microphones. Device labels only appear once mic permission has been granted.
  const loadDevices = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) return
    try {
      const all = await navigator.mediaDevices.enumerateDevices()
      const mics = all.filter((device) => device.kind === 'audioinput' && device.deviceId)
      setMicrophones(mics)
      setSelectedDeviceId((current) => {
        if (current && mics.some((mic) => mic.deviceId === current)) return current
        return mics[0]?.deviceId ?? ''
      })
    } catch {
      // ignore enumeration errors
    }
  }, [])

  useEffect(() => {
    void loadDevices()
    const media = navigator.mediaDevices
    if (!media?.addEventListener) return
    const handler = () => void loadDevices()
    media.addEventListener('devicechange', handler)
    return () => media.removeEventListener('devicechange', handler)
  }, [loadDevices])

  // Keep the latest object URL in a ref so unmount cleanup can revoke it.
  useEffect(() => {
    recordedUrlRef.current = recordedUrl
  }, [recordedUrl])

  useEffect(() => {
    return () => {
      clearTimer()
      stopStream()
      audioContextRef.current?.close().catch(() => {})
      sampleAudioRef.current?.pause()
      if (recordedUrlRef.current) URL.revokeObjectURL(recordedUrlRef.current)
    }
  }, [])

  const resetRecording = () => {
    if (recordedUrl) URL.revokeObjectURL(recordedUrl)
    setRecordedBlob(null)
    setRecordedUrl(null)
    setElapsed(0)
  }

  const startRecording = async () => {
    if (typeof MediaRecorder === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      addToast('Voice recording is not supported in this browser', 'error')
      return
    }
    resetRecording()
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: selectedDeviceId ? { deviceId: { exact: selectedDeviceId } } : true,
      })
      streamRef.current = stream
      // Now that permission is granted, re-enumerate so device names are available.
      void loadDevices()

      // Live input meter so the user can see the mic is picking up sound.
      const AudioContextClass =
        window.AudioContext ||
        (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
      if (AudioContextClass) {
        const audioContext = new AudioContextClass()
        audioContextRef.current = audioContext
        const source = audioContext.createMediaStreamSource(stream)
        const analyserNode = audioContext.createAnalyser()
        analyserNode.fftSize = 256
        analyserNode.smoothingTimeConstant = 0.7
        source.connect(analyserNode) // not connected to destination — avoids echo/feedback
        setAnalyser(analyserNode)
      }

      chunksRef.current = []
      const recorder = new MediaRecorder(stream)
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data)
      }
      recorder.onstop = () => {
        const type = recorder.mimeType || 'audio/webm'
        const blob = new Blob(chunksRef.current, { type })
        setRecordedBlob(blob)
        setRecordedUrl(URL.createObjectURL(blob))
        stopStream()
      }
      mediaRecorderRef.current = recorder
      recorder.start()
      setIsRecording(true)
      setElapsed(0)
      timerRef.current = window.setInterval(() => setElapsed((value) => value + 1), 1000)
    } catch {
      addToast('Microphone access was denied or unavailable', 'error')
      stopStream()
    }
  }

  const stopRecording = () => {
    clearTimer()
    setIsRecording(false)
    mediaRecorderRef.current?.stop()
    mediaRecorderRef.current = null
    teardownAudioAnalyser()
  }

  // Abandon the recording flow and return to the overview (discards any in-progress recording).
  const cancelRecording = () => {
    clearTimer()
    stopStream()
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.onstop = null
      mediaRecorderRef.current.stop()
    }
    mediaRecorderRef.current = null
    teardownAudioAnalyser()
    setIsRecording(false)
    resetRecording()
    setVoiceName('')
    setIsMale(true)
    setConsent(false)
    setMode('overview')
  }

  // Add: start a blank recording flow. Replace/update: prefill with the current voice's details.
  const beginAdd = () => {
    resetRecording()
    setVoiceName('')
    setIsMale(true)
    setConsent(false)
    setMode('record')
  }

  const beginReplace = () => {
    sampleAudioRef.current?.pause()
    resetRecording()
    setVoiceName(currentVoice?.voiceName ?? '')
    setIsMale(currentVoice?.isMale ?? true)
    setConsent(false)
    setMode('record')
  }

  // Play/pause the cloned voice's S3 sample (presigned sampleUrl from the API).
  const togglePlaySample = () => {
    const url = currentVoice?.sampleUrl
    if (!url) {
      addToast('No sample available for this voice', 'info')
      return
    }
    let audio = sampleAudioRef.current
    if (!audio) {
      audio = new Audio()
      audio.onended = () => setIsPlayingSample(false)
      audio.onpause = () => setIsPlayingSample(false)
      sampleAudioRef.current = audio
    }
    if (isPlayingSample) {
      audio.pause()
      return
    }
    if (audio.src !== url) audio.src = url
    void audio
      .play()
      .then(() => setIsPlayingSample(true))
      .catch(() => {
        setIsPlayingSample(false)
        addToast('Could not play the voice sample', 'error')
      })
  }

  const handleSubmit = async () => {
    const hasNewRecording = recordedBlob !== null
    // Adding requires a sample; updating can change just the name/gender without re-recording.
    if (!isUpdate && !hasNewRecording) {
      addToast('Please record a voice sample first', 'warning')
      return
    }
    // Voice name and consent are always required.
    if (!voiceName.trim()) {
      addToast('Please enter a voice name', 'warning')
      return
    }
    if (!consent) {
      addToast('Please confirm you have consent to clone this voice', 'warning')
      return
    }
    let file: File | null = null
    if (hasNewRecording && recordedBlob) {
      const ext = extensionForMime(recordedBlob.type)
      file = new File([recordedBlob], `${voiceName.trim()}.${ext}`, { type: recordedBlob.type })
    }
    try {
      await cloneVoiceMutation.mutateAsync({
        file,
        isMale,
        voiceName: voiceName.trim(),
      })
      addToast(isUpdate ? 'Voice updated' : 'Voice submitted for cloning', 'success')
      resetRecording()
      setVoiceName('')
      setIsMale(true)
      setConsent(false)
      setMode('overview')
      onVoiceSaved?.()
    } catch (error) {
      addToast(error instanceof Error ? error.message : 'Failed to submit voice', 'error')
    }
  }

  const hasNewRecording = recordedBlob !== null
  const tooShort = hasNewRecording && elapsed < MIN_SECONDS
  // Voice name and consent always required; a recording is required only when adding.
  const canSubmit =
    !isRecording && consent && voiceName.trim().length > 0 && (isUpdate || hasNewRecording)

  if (mode === 'overview') {
    return (
      <div className="space-y-6 border border-gray-200 rounded-lg bg-white p-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Clone a Voice</h3>
          <p className="text-sm text-gray-600">Your cloned voice is ready to use in AdvicePrep videos.</p>
        </div>

        {clonesLoading ? (
          <div className="flex justify-center py-12">
            <Spin size="large" />
          </div>
        ) : currentVoice ? (
          <>
            <div className="flex items-center gap-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div
                className="w-14 h-14 rounded-full bg-white border-2 flex items-center justify-center"
                style={{ borderColor: 'var(--feature-primary)' }}
              >
                <AudioOutlined className="text-2xl" style={{ color: 'var(--feature-primary)' }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-base font-semibold text-gray-900 truncate">{currentVoice.voiceName}</span>
                  {currentProcessing ? (
                    <span className="inline-flex items-center gap-1.5">
                      <Tag color="gold" className="m-0">
                        {currentVoice.status}
                      </Tag>
                      <Spin size="small" />
                    </span>
                  ) : /ready|complete|success/i.test(currentVoice.status) ? (
                    <Tag color="green" icon={<CheckCircleOutlined />}>
                      {currentVoice.status}
                    </Tag>
                  ) : (
                    <Tag color="red">{currentVoice.status}</Tag>
                  )}
                </div>
                <p className="text-sm text-gray-600">
                  {currentVoice.isMale ? 'Male' : 'Female'} · Cloned voice
                </p>
                {currentVoice.errorMessage ? (
                  <p className="text-sm text-red-600">{currentVoice.errorMessage}</p>
                ) : null}
              </div>
              <Button
                icon={isPlayingSample ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                disabled={currentProcessing || !currentVoice.sampleUrl}
                onClick={togglePlaySample}
              >
                {isPlayingSample ? 'Pause' : 'Play sample'}
              </Button>
            </div>

            <div className="flex justify-end">
              <Button icon={<ReloadOutlined />} onClick={beginReplace}>
                Replace voice
              </Button>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center gap-4 rounded-lg border border-dashed border-gray-300 bg-gray-50 py-12">
            <div className="w-14 h-14 rounded-full bg-white border-2 border-gray-200 flex items-center justify-center">
              <AudioOutlined className="text-2xl text-gray-400" />
            </div>
            <p className="text-sm text-gray-600">No voice added yet.</p>
            <Button type="primary" className="feature-button-solid" icon={<PlusOutlined />} onClick={beginAdd}>
              Add
            </Button>
          </div>
        )}
      </div>
    )
  }

  // While saving, show a spinner in place of the form (stays on this view; no page reload).
  // On 200 we switch to overview; on error we fall back here with the inputs preserved.
  if (cloneVoiceMutation.isPending) {
    return (
      <div className="space-y-6 border border-gray-200 rounded-lg bg-white p-6">
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <Spin size="large" />
          <p className="text-sm text-gray-500">Saving your voice…</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 border border-gray-200 rounded-lg bg-white p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Record a Voice</h3>
          <p className="text-sm text-gray-600">
            Record a clear voice sample to create a voice clone for your AdvicePrep videos.
          </p>
        </div>
        <Button onClick={cancelRecording}>Cancel</Button>
      </div>

      {isUpdate ? (
        <Alert
          type="info"
          showIcon
          message="Re-recording is optional. Leave the recorder to keep the current voice sample and only update the name or gender."
        />
      ) : null}

      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
        <p className="text-sm font-medium text-gray-700 mb-2">Read this aloud while recording</p>
        <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">{READING_SCRIPT}</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Microphone</label>
        <Select
          className="max-w-md w-full"
          value={selectedDeviceId || undefined}
          onChange={setSelectedDeviceId}
          disabled={isRecording}
          placeholder="Default microphone"
          options={microphones.map((mic, index) => ({
            value: mic.deviceId,
            label: mic.label || `Microphone ${index + 1}`,
          }))}
          notFoundContent="No microphones detected"
        />
        {microphones.length === 0 ? (
          <p className="mt-1 text-xs text-gray-500">
            Allow microphone access (start a recording once) to choose a specific device.
          </p>
        ) : null}
      </div>

      <div className="flex flex-col items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 py-5 px-4">
        <div className="flex items-center gap-2 h-5">
          {isRecording ? (
            <>
              <span className="inline-block w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
              <span className="text-sm font-medium text-red-600">Recording…</span>
            </>
          ) : recordedBlob ? (
            <span className="text-sm text-gray-500">Recording ready — review it below.</span>
          ) : (
            <span className="text-sm text-gray-400">Press start, then read the script above.</span>
          )}
        </div>

        <MicVisualizer analyser={analyser} active={isRecording} />

        <div className="text-3xl font-mono tabular-nums text-gray-800">{formatDuration(elapsed)}</div>

        {!isRecording ? (
          <Button
            type="primary"
            className="feature-button-solid"
            icon={<AudioOutlined />}
            onClick={() => void startRecording()}
          >
            {recordedBlob ? 'Re-record' : 'Start Recording'}
          </Button>
        ) : (
          <Button danger icon={<BorderOutlined />} onClick={stopRecording}>
            Stop Recording
          </Button>
        )}
      </div>

      {recordedUrl && !isRecording ? (
        <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Preview your recording</span>
            <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={resetRecording}>
              Discard
            </Button>
          </div>
          <audio src={recordedUrl} controls className="w-full" />
        </div>
      ) : null}

      {tooShort ? (
        <Alert
          type="warning"
          showIcon
          message={`For best results, record at least ${MIN_SECONDS} seconds (1–2 minutes recommended).`}
        />
      ) : null}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Voice name</label>
        <Input
          value={voiceName}
          onChange={(e) => setVoiceName(e.target.value)}
          placeholder="e.g. John's Advisory Voice"
          maxLength={80}
          className="max-w-md"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
        <Radio.Group value={isMale} onChange={(e) => setIsMale(e.target.value)}>
          <Radio value={true}>Male</Radio>
          <Radio value={false}>Female</Radio>
        </Radio.Group>
      </div>

      <Checkbox checked={consent} onChange={(e) => setConsent(e.target.checked)}>
        I confirm this is my own voice and I consent to it being cloned for my AdvicePrep videos.
      </Checkbox>

      <div className="space-y-1 text-sm text-gray-600">
        <p>Tips: record in a quiet room, speak naturally, and aim for 1–2 minutes of continuous speech.</p>
      </div>

      <div className="flex justify-end gap-2">
        <Button onClick={cancelRecording} disabled={cloneVoiceMutation.isPending}>
          Cancel
        </Button>
        <Button
          type="primary"
          className="feature-button-solid"
          onClick={() => void handleSubmit()}
          loading={cloneVoiceMutation.isPending}
          disabled={!canSubmit}
        >
          Submit
        </Button>
      </div>
    </div>
  )
}
